import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import PieChart from "@cloudscape-design/components/pie-chart";
import CONFIG from "../config";

function Dashboard({ idToken, onNavigate, onLogout }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    totalDocuments: 0,
    pendingDocuments: 0,
    processingDocuments: 0,
    completedDocuments: 0,
    failedDocuments: 0,
  });
  const [byDocumentType, setByDocumentType] = useState([]);
  const [byFileType, setByFileType] = useState([]);
  const [detectionStats, setDetectionStats] = useState({ autoDetected: 0, userSpecified: 0 });
  const [loading, setLoading] = useState(true);

  const handleUnauthorized = useCallback(() => {
    if (onLogout) {
      onLogout();
    }
  }, [onLogout]);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      // Load documents and document types in parallel
      const [docsResponse, typesResponse] = await Promise.all([
        fetch(`${CONFIG.apiUrl}documents`, { headers: { Authorization: idToken } }),
        fetch(`${CONFIG.apiUrl}document-types`, { headers: { Authorization: idToken } }),
      ]);

      if (docsResponse.status === 401 || docsResponse.status === 403 || typesResponse.status === 401 || typesResponse.status === 403) {
        handleUnauthorized();
        return;
      }

      if (docsResponse.ok && typesResponse.ok) {
        const docsData = await docsResponse.json();
        const typesData = await typesResponse.json();
        const documents = docsData.documents || [];
        const documentTypes = typesData.document_types || [];

        // Create a map of document type IDs to names
        const typeMap = {};
        documentTypes.forEach((type) => {
          typeMap[type.id] = type.name;
        });

        const stats = {
          totalDocuments: documents.length,
          pendingDocuments: documents.filter((d) => d.processing_status === "pending").length,
          processingDocuments: documents.filter((d) => d.processing_status === "processing").length,
          completedDocuments: documents.filter((d) => d.processing_status === "completed").length,
          failedDocuments: documents.filter((d) => d.processing_status === "failed").length,
        };

        // Calculate by document type
        const docTypeMap = {};
        documents.forEach((doc) => {
          const typeName = doc.document_type_id ? typeMap[doc.document_type_id] || "Unknown Type" : "Unspecified";
          docTypeMap[typeName] = (docTypeMap[typeName] || 0) + 1;
        });
        const docTypeStats = Object.entries(docTypeMap)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count);

        // Calculate by file type
        const fileTypeMap = {};
        documents.forEach((doc) => {
          const ext = doc.file_name?.split(".").pop()?.toUpperCase() || "Unknown";
          fileTypeMap[ext] = (fileTypeMap[ext] || 0) + 1;
        });
        const fileTypeStats = Object.entries(fileTypeMap)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count);

        // Calculate auto-detected vs user-specified
        const autoDetected = documents.filter((d) => d.type_auto_detected === true).length;
        const userSpecified = documents.length - autoDetected;

        setStats(stats);
        setByDocumentType(docTypeStats);
        setByFileType(fileTypeStats);
        setDetectionStats({ autoDetected, userSpecified });
      }
    } catch (error) {
      console.error("Failed to load stats:", error);
      if (error.message === "Failed to fetch") {
        handleUnauthorized();
      }
    } finally {
      setLoading(false);
    }
  }, [idToken, handleUnauthorized]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const StatBox = ({ label, value, color = "inherit" }) => (
    <Box textAlign="center">
      <Box variant="awsui-key-label">{label}</Box>
      <Box fontSize="display-l" fontWeight="bold" color={color}>
        {loading ? "-" : value}
      </Box>
    </Box>
  );

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description={t("venue.dashboard.statisticsDescription")}
            actions={
              <Button iconName="refresh" onClick={loadStats} loading={loading}>
                {t("venue.common.refresh")}
              </Button>
            }
          >
            {t("venue.dashboard.documentProcessingStatistics")}
          </Header>
        }
      >
        <ColumnLayout columns={5} variant="text-grid">
          <StatBox label={t("venue.dashboard.totalDocuments")} value={stats.totalDocuments} />
          <StatBox label={t("venue.dashboard.pending")} value={stats.pendingDocuments} color="text-status-info" />
          <StatBox label={t("venue.dashboard.processing")} value={stats.processingDocuments} color="text-status-info" />
          <StatBox label={t("venue.dashboard.completed")} value={stats.completedDocuments} color="text-status-success" />
          <StatBox label={t("venue.dashboard.failed")} value={stats.failedDocuments} color="text-status-error" />
        </ColumnLayout>
      </Container>

      <ColumnLayout columns={3} variant="text-grid">
        <Container header={<Header variant="h3">{t("venue.dashboard.byDocumentType")}</Header>}>
          {loading ? (
            <Box textAlign="center">-</Box>
          ) : byDocumentType.length > 0 ? (
            <PieChart
              data={byDocumentType.map((item) => ({
                title: item.name,
                value: item.count,
              }))}
              size="medium"
              hideFilter
              hideLegend={false}
              variant="donut"
              ariaLabel="Documents by type"
              ariaDescription="Pie chart showing distribution of documents by type"
            />
          ) : (
            <Box textAlign="center" color="text-status-inactive">
              {t("venue.dashboard.noDocumentsYet")}
            </Box>
          )}
        </Container>

        <Container header={<Header variant="h3">{t("venue.dashboard.detectionMethod")}</Header>}>
          {loading ? (
            <Box textAlign="center">-</Box>
          ) : stats.totalDocuments > 0 ? (
            <PieChart
              data={[
                { title: t("venue.dashboard.autoDetected"), value: detectionStats.autoDetected },
                { title: t("venue.dashboard.userSpecified"), value: detectionStats.userSpecified },
              ]}
              size="medium"
              hideFilter
              hideLegend={false}
              variant="donut"
              ariaLabel="Detection method distribution"
              ariaDescription="Pie chart showing auto-detected vs user-specified document types"
            />
          ) : (
            <Box textAlign="center" color="text-status-inactive">
              {t("venue.dashboard.noDocumentsYet")}
            </Box>
          )}
        </Container>

        <Container header={<Header variant="h3">{t("venue.dashboard.byFileType")}</Header>}>
          {loading ? (
            <Box textAlign="center">-</Box>
          ) : byFileType.length > 0 ? (
            <PieChart
              data={byFileType.map((item) => ({
                title: item.name,
                value: item.count,
              }))}
              size="medium"
              hideFilter
              hideLegend={false}
              variant="donut"
              ariaLabel="Documents by file type"
              ariaDescription="Pie chart showing distribution of documents by file type"
            />
          ) : (
            <Box textAlign="center" color="text-status-inactive">
              {t("venue.dashboard.noDocumentsYet")}
            </Box>
          )}
        </Container>
      </ColumnLayout>

      <Container header={<Header variant="h2">{t("venue.dashboard.quickActions")}</Header>}>
        <SpaceBetween size="m">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="primary" iconName="upload" onClick={() => onNavigate && onNavigate("documents")}>
              {t("venue.dashboard.uploadDocuments")}
            </Button>
            <Button iconName="settings" onClick={() => onNavigate && onNavigate("document-types")}>
              {t("venue.dashboard.manageDocumentTypes")}
            </Button>
            <Button onClick={() => onNavigate && onNavigate("documents")}>{t("venue.dashboard.viewAllDocuments")}</Button>
          </SpaceBetween>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
}

export default Dashboard;
